import { MediaObject } from '@anm/HostingPlayer/types';
import { ActionOption } from '@anm/components/modals/ActionSheet';
import getExtensionFromPath from '@anm/helpers/getExtensionFromPath';
import isId from '@anm/helpers/is/isId';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { useEffect, useState, RefObject } from 'react';
import { useSelector } from 'react-redux';

import { hostedVideoSelectors } from '../../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../../store/modules/project';
import { streamSelectors } from '../../../../store/modules/stream';
import { userSelectors } from '../../../../store/modules/user';

import { DropdownDirection } from './Wrapper';
import { getDownloadSources, getFormattedName } from './helpers';

const useDownloadSources = () => {
  const videoData = useSelector(hostedVideoSelectors.selectVideoData);
  const downloadSources = getDownloadSources(videoData?.sources);

  return downloadSources;
};

export const useFileName = (name?: string) => {
  const videoName = useSelector(projectSelectors.selectCurrentProjectName);
  const fileName = getFormattedName(name || videoName);

  return fileName;
};

export type DownloadProps = {
  onStreamDownload: (streamId?: string, uploadId?: string) => void;
};

export const useDownloadOptionsMobile = ({ onStreamDownload }: DownloadProps) => {
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const [showInfoOnMobile, setShowInfoOnMobile] = useState(false);
  // const isUserFree = useSelector(userSelectors.selectIsUserFree);
  const audio = useSelector(hostedVideoSelectors.selectVideoAudio);
  const downloadSources = useDownloadSources();

  const streamId = useSelector(streamSelectors.selectStreamId);

  const videoOptions = downloadSources.length
    ? downloadSources.map(({ width, height, path, isOriginal }) => ({
        label: isOriginal ? 'Original file' : `${width}x${height}`,
        value: path
      }))
    : [];

  const audioSource = {
    label: 'Download audio',
    value: audio?.path as string
  };

  let timer: NodeJS.Timeout;

  useEffect(() => {
    if (showInfoOnMobile) {
      timer = global.setTimeout(() => setShowInfoOnMobile(false), 10000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [showInfoOnMobile]);

  const handleActionSheetChange = (option: ActionOption) => {
    setIsActionSheetOpen(false);
    setShowInfoOnMobile(true);
    const { value: path } = option;
    const downloadSources = useDownloadSources();
    const { url, extension } = useDownloadFileUrl(downloadSources.find(s => s.path === path)!);
    const videoExension = !isId(path) && extension;
    const isStreamVideo = videoExension === 'm3u8';

    if (isStreamVideo && streamId) {
      onStreamDownload(streamId);
      return;
    }

    window.open(url, '_self');
  };

  const actionSheetOptions = [...videoOptions];

  if (audio) actionSheetOptions.push(audioSource);

  return [
    // isUserFree,
    showInfoOnMobile,
    isActionSheetOpen,
    actionSheetOptions,
    setShowInfoOnMobile,
    setIsActionSheetOpen,
    handleActionSheetChange
  ] as const;
};

export const useDownloadFileUrl = (media?: MediaObject, projectName?: string) => {
  const fileName = useFileName(projectName);

  const extension = getExtensionFromPath(media?.path || '');

  const sufix = media?.width && media.height ? `_${media.width}x${media.height}` : '';

  return {
    url: `${media?.path}?filename=${fileName}${sufix}.${extension}`,
    extension
  };
};

export const useDownloadOptionsDesktop = () => {
  const audio = useSelector(hostedVideoSelectors.selectVideoAudio);
  const isUserFree = useSelector(userSelectors.selectIsUserFree);
  const downloadSources = useDownloadSources();

  const videoSources = downloadSources.map(media => {
    const { url, extension } = useDownloadFileUrl(media);
    const isStreamVideo = extension === 'm3u8';
    const label = media.isOriginal ? 'Original file' : `${media.width}x${media.height}`;

    return {
      label,
      value: media.path,
      href: isStreamVideo ? undefined : url
    };
  });

  const options = [...videoSources];

  if (audio?.path) {
    const audioSource = {
      label: 'Download audio',
      value: audio?.path,
      href: useDownloadFileUrl(audio as MediaObject).url
    };

    options.push(audioSource);
  }

  return [isUserFree, options!] as const;
};

type UseDropdownDirectionProps = {
  ref: RefObject<HTMLDivElement>;
  isHovered: boolean;
};

export const useDropdownDirection = ({ ref, isHovered }: UseDropdownDirectionProps) => {
  const [optionsHeight, setOptionsHeight] = useState<number>(0);
  const [dropdownDirection, setDropdownDirection] = useState<DropdownDirection>('top');

  useEffect(() => {
    if (!isHovered || isMobileOrIPad()) return;

    const bounding = ref.current?.getBoundingClientRect();

    const dropdownBottomPosition = bounding?.bottom || 0;

    const direction = document.documentElement.clientHeight - dropdownBottomPosition < optionsHeight ? 'top' : 'bottom';

    setDropdownDirection(direction);
  }, [isHovered, optionsHeight]);

  return [dropdownDirection, setOptionsHeight] as const;
};
